// De interface rond het spel: schermen (start, pauze, afrekening, garage, instellingen, uitleg, boete),
// de HUD tijdens het rijden, aanbiedingen, lading, meldingen en het bonnetje na een levering.
import { SHIFT_SECONDS, RACE_BONUS, clockLabel } from '../game/shift';
import { timeLeft } from '../game/orders';
import { DISTRICT_NAMES, PAINTS, RANKS, UPGRADES, capacityFor, euro, euroShort, nextRank, rankFor } from '../game/economy';
import { ACHIEVEMENTS } from '../game/achievements';
import { guideStep } from '../game/guide';
import { analyticsEnabled, getConsent, setConsent, subscribeConsent, trackPage } from '../utils/analytics';
import { buyUpgrade, equip, todayKey, whyNotBuy } from '../state/save';
import { parseShare, shareUrl } from '../state/share';
import { SOUND_SETTINGS, levelGain } from '../audio/mix';
import { meterColors } from '../game/urgency';
import { fmtNum, getLanguage, resolveLanguage, setLanguage, t, tn } from '../i18n';
import { applyDocumentLanguage, applyStatic } from '../i18n/dom';

/** Hoe belangrijk een melding is, en hoe lang hij blijft staan (ms). */
const TOAST_PRIO = { info: 0, good: 1, warn: 2, bad: 3 };
const TOAST_MS = { info: 1800, good: 2200, warn: 2600, bad: 2400 };

const $ = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const districtList = (ids) => ids.map((d) => DISTRICT_NAMES[d]).join(t('list.and'));
const starString = (n, max = 5) => '★'.repeat(n) + '☆'.repeat(max - n);

export class Ui {
  constructor() {
    this.game = null;
    this.stack = [];
    this.lastHud = 0;
    this.offerIds = '';
    this.cargoIds = '';
    this.updateFn = null;
    this.installPrompt = null;
    this.bindScreens();
    this.bindHud();
    this.bindAnalytics();
  }

  /** Toont het startscherm al voordat het spel (de stad) is opgebouwd. */
  preview(save) {
    this.save = save;
    this.open('start');
  }

  /**
   * Zet de taal van de hele interface: vaste teksten, paginatitel, en alles wat het spel zelf tekent.
   * 'auto' volgt de taal van het apparaat. Aanroepen bij het starten en bij elke wissel in de instellingen.
   */
  applyLanguage(setting) {
    setLanguage(resolveLanguage(setting, navigator.languages?.length ? navigator.languages : [navigator.language]));
    applyDocumentLanguage();
    applyStatic();
    this.offerIds = '';
    this.cargoIds = '';
    if (this.game) this.onModeChange(this.game.mode);
    for (const name of this.stack) {
      if (name === 'garage') this.renderGarage();
      if (name === 'achievements') this.renderAchievements();
    }
    this.renderStart();
    this.renderSettings();
    this.renderUpdateLine();
    $('btn-stop').textContent = this.game?.mode === 'freeride' ? t('pause.stopFreeride') : t('pause.stopShift');
  }

  /** Kleurenblindveilige stand: de pagina krijgt een klasse (balkjes in de lading), het canvas leest de instelling zelf. */
  applyColorblind(on) {
    document.documentElement.classList.toggle('colorblind', !!on);
    this.cargoIds = ''; // balkjes opnieuw opbouwen met de juiste kleuren
  }

  attach(game) {
    this.game = game;
    this.applyColorblind(game.save.settings.colorblind);
    this.renderSettings();
    this.renderStart();
    if (!this.stack.length) this.open('start');
  }

  // ---------------------------------------------------------------------------
  // Schermen
  // ---------------------------------------------------------------------------

  open(name) {
    const el = $(`screen-${name}`);
    if (!el) return;
    if (this.stack.at(-1) !== name) {
      for (const n of this.stack) $(`screen-${n}`)?.classList.remove('is-open');
      this.stack.push(name);
    }
    $('screens').classList.remove('hidden');
    $('screens').classList.add('flex');
    el.classList.add('is-open');
    trackPage(`/${name}`, el.querySelector('h1, h2')?.textContent || name);
    this.renderAnalytics();
    if (name === 'garage') this.renderGarage();
    if (name === 'achievements') this.renderAchievements();
    if (name === 'start') this.renderStart();
    if (name === 'settings') this.renderSettings();
    requestAnimationFrame(() => el.querySelector('.btn-primary, button')?.focus({ preventScroll: true }));
  }

  /** Sluit het bovenste scherm en toont het vorige, of gaat terug naar het spel. */
  close() {
    const name = this.stack.pop();
    if (name) $(`screen-${name}`)?.classList.remove('is-open');
    const prev = this.stack.at(-1);
    if (prev) {
      this.stack.pop();
      this.open(prev);
      return;
    }
    $('screens').classList.add('hidden');
    $('screens').classList.remove('flex');
    this.renderAnalytics();
  }

  closeAll() {
    for (const n of this.stack) $(`screen-${n}`)?.classList.remove('is-open');
    this.stack = [];
    $('screens').classList.add('hidden');
    $('screens').classList.remove('flex');
    this.renderAnalytics();
  }

  bindScreens() {
    const on = (id, fn) => $(id)?.addEventListener('click', () => {
      if (!this.game) return; // de stad wordt nog opgebouwd (duurt een fractie van een seconde)
      this.game.sound.init();
      this.game?.sound.playUiClick?.();
      fn();
    });
    on('btn-start-shift', () => this.startShift());
    on('btn-guide-skip', () => this.game.finishGuide(false));
    on('btn-resume-shift', () => {
      this.closeAll();
      if (!this.game.resumeSavedShift()) this.renderStart();
    });
    // Na de uitdaging van de dag volgt dezelfde uitdaging opnieuw (zelfde bestellingen: je kunt je score verbeteren)
    on('btn-next-shift', () => {
      this.closeAll();
      this.game.startShift({ daily: this.lastWasDaily });
    });
    on('btn-freeride', () => {
      this.closeAll();
      this.game.startFreeRide();
    });
    on('btn-daily', () => {
      this.closeAll();
      this.game.startShift({ daily: true });
    });
    on('btn-open-achievements', () => this.open('achievements'));
    on('btn-open-garage', () => this.open('garage'));
    on('btn-report-garage', () => this.open('garage'));
    on('btn-open-settings', () => this.open('settings'));
    on('btn-pause-settings', () => this.open('settings'));
    on('btn-open-help', () => this.open('help'));
    on('btn-pause-help', () => this.open('help'));
    on('btn-report-menu', () => {
      this.closeAll();
      this.open('start');
    });
    on('btn-resume', () => this.game.resume());
    on('btn-stop', () => {
      this.closeAll();
      this.game.paused = false;
      this.game.endShift();
    });
    on('btn-pay-fine', () => {
      this.close();
      this.game.payPoliceFine(false);
    });
    on('btn-contest-fine', () => {
      this.close();
      this.game.payPoliceFine(true);
    });
    on('btn-install', () => this.install());
    on('btn-share', () => this.shareScore());
    on('btn-share-play', () => this.playShared());

    for (const btn of document.querySelectorAll('[data-close]')) {
      btn.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', (e) => {
      if (!this.stack.length) return;
      const top = this.stack.at(-1);
      if (e.key === 'Escape') {
        e.preventDefault();
        if (top === 'pause') this.game.resume();
        else if (top === 'fine' || top === 'start' || top === 'report') return;
        else this.close();
      }
      if (e.key === 'Tab') this.trapFocus(e, $(`screen-${top}`));
    });

    for (const btn of document.querySelectorAll('[data-setting]')) {
      btn.addEventListener('click', () => this.changeSetting(btn.dataset.setting, btn.dataset.value));
    }
  }

  /** Toestemming voor anonieme statistieken: kleine melding onderin (alleen op het startscherm) en een keuze in de instellingen. */
  bindAnalytics() {
    $('btn-analytics-allow').addEventListener('click', () => setConsent('granted'));
    $('btn-analytics-deny').addEventListener('click', () => setConsent('denied'));
    for (const btn of document.querySelectorAll('[data-analytics]')) {
      btn.addEventListener('click', () => setConsent(btn.dataset.analytics));
    }
    subscribeConsent(() => this.renderAnalytics());
  }

  renderAnalytics() {
    const ask = $('analytics-ask');
    if (!ask) return;
    const consent = getConsent();
    const show = analyticsEnabled() && consent === 'unset' && this.stack.at(-1) === 'start';
    ask.classList.toggle('hidden', !show);
    ask.classList.toggle('flex', show);
    for (const btn of document.querySelectorAll('[data-analytics]')) {
      btn.setAttribute('aria-pressed', String(btn.dataset.analytics === (consent === 'granted' ? 'granted' : 'denied')));
    }
    $('analytics-setting').classList.toggle('hidden', !analyticsEnabled());
  }

  trapFocus(e, root) {
    const items = [...root.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent);
    if (!items.length) return;
    const first = items[0];
    const last = items.at(-1);
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  startShift() {
    this.closeAll();
    this.game.startShift();
  }

  renderStart() {
    const s = this.game?.save ?? this.save;
    if (!s) return;
    const rank = rankFor(s.stars);
    const next = nextRank(s.stars);
    $('start-rank-label').textContent = t('start.rank', { rank: rank.rank, total: RANKS.length });
    $('start-rank-title').textContent = rank.title;
    $('start-wallet').textContent = `👛 ${euro(s.wallet)}`;
    $('start-stars').textContent = t('start.stars', { stars: tn('count.stars', s.stars) });
    $('start-best').textContent = s.stats.bestEarnings > 0 ? t('start.bestDay', { money: euro(s.stats.bestEarnings) }) : t('start.noBestDay');
    if (next) {
      const pct = ((s.stars - rank.from) / (next.from - rank.from)) * 100;
      $('start-rank-bar').style.width = `${Math.round(pct)}%`;
      $('start-rank-next').textContent = t('start.nextRank', {
        stars: tn('count.stars', next.from - s.stars),
        title: next.title,
        districts: districtList(next.districts.filter((d) => !rank.districts.includes(d))),
      });
    } else {
      $('start-rank-bar').style.width = '100%';
      $('start-rank-next').textContent = t('start.maxRank');
    }
    // Een bewaarde shift: verder waar je gebleven was (dan is nieuw starten de tweede keus)
    const saved = this.game?.savedShift;
    $('btn-resume-shift').classList.toggle('hidden', !saved);
    $('btn-resume-shift').classList.toggle('flex', !!saved);
    $('btn-start-shift').classList.toggle('btn-primary', !saved);
    $('btn-start-shift').classList.toggle('btn-secondary', !!saved);
    $('btn-start-shift').classList.toggle('mt-5', !saved);
    $('btn-start-shift').classList.toggle('mt-3', !!saved);
    if (saved) $('resume-status').textContent = t('start.resumeStatus', { time: clockLabel(saved.snapshot.t), money: euro(saved.snapshot.earnings) });
    $('btn-start-shift').textContent = s.stats.shifts === 0 ? t('start.startFirst') : t('start.start');
    // Uitdaging van de dag: dezelfde bestellingen voor iedereen; beste resultaat van vandaag
    const today = s.daily.date === todayKey() ? s.daily : null;
    $('daily-status').textContent = today?.played ? t('start.dailyBest', { money: euro(today.best) }) : t('start.dailyEveryone');
  }

  // ---------------------------------------------------------------------------
  // Callbacks vanuit het spel
  // ---------------------------------------------------------------------------

  onModeChange(mode) {
    const playing = mode === 'shift' || mode === 'freeride';
    $('hud').classList.toggle('hidden', !playing);
    $('hud').dataset.mode = mode;
    for (const id of ['hud-clock-pill', 'hud-offers', 'hud-cargo']) $(id).classList.toggle('hidden', mode !== 'shift');
    // In een shift staat er alleen klok, dagverdienste en de pauzeknop bovenin; de portemonnee zie je buiten de shift.
    $('hud-wallet-pill').classList.toggle('hidden', mode !== 'freeride');
    $('hud-offers').innerHTML = '';
    $('hud-cargo').innerHTML = '';
    this.offerIds = '';
    this.cargoIds = '';
    $('pause-sub').textContent = mode === 'freeride' ? t('pause.subFreeride') : t('pause.subShift');
    $('btn-stop').textContent = mode === 'freeride' ? t('pause.stopFreeride') : t('pause.stopShift');
    if (!playing && !this.stack.length) this.open('start');
    this.onResize(this.game);
  }

  onPause(paused) {
    if (paused) this.open('pause');
    else this.closeAll();
  }

  onResize(game) {
    if (!game) return;
    const top = $('hud-top').getBoundingClientRect();
    game.safeTop = top.bottom > 0 ? top.bottom + 8 : 70;
    const controls = $('touch-controls');
    const ctlTop = controls.offsetParent ? controls.getBoundingClientRect().top : innerHeight;
    this.baseSafeBottom = Math.max(24, innerHeight - ctlTop + 12);
    game.safeBottom = this.baseSafeBottom;
  }

  onOffer(order) {
    this.game?.vibrate(25);
    this.renderOffers(true);
  }

  showReceipt(order, r, combo) {
    const parts = [
      `<span>${r.late ? t('receipt.late') : t('receipt.ontime')}</span>`,
      r.tempScore !== null ? `<span>${t(order.product.heat === 'koud' ? 'receipt.cold' : 'receipt.warm', { pct: r.tempScore })}</span>` : '',
      `<span>${t('receipt.care', { pct: r.careScore })}</span>`,
      combo >= 2 ? `<span class="text-orange-600 dark:text-orange-400">${t('receipt.combo', { n: combo })}</span>` : '',
    ].join('');
    const el = document.createElement('div');
    el.className = 'receipt';
    el.innerHTML = `
      <div class="flex items-center justify-between gap-3">
        <span class="text-lg tracking-wider text-amber-500" aria-label="${t('receipt.starsAria', { n: r.stars })}">${starString(r.stars)}</span>
        <span class="font-mono text-lg font-extrabold text-emerald-700 dark:text-emerald-300">+${euro(r.total)}</span>
      </div>
      <div class="mt-0.5 flex items-center justify-between gap-3 text-sm font-bold">
        <span class="truncate">${order.product.icon} ${esc(order.customer)}</span>
        <span class="whitespace-nowrap">+${r.stars} ★${order.race ? ` · 🏁 +${euro(RACE_BONUS)}` : ''}</span>
      </div>
      <div class="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs font-semibold text-stone-600 dark:text-stone-300">${parts}</div>`;
    this.pushToast(el, 3200);
  }

  /**
   * Meldingen komen één voor één, de belangrijkste eerst. Een gewone melding (info) verdwijnt zonder
   * wachtrij als er al iets te zien is; een gevaarlijke (bad) schuift voor. Dezelfde tekst dubbel telt één keer.
   */
  toast(text, kind = 'info') {
    const el = document.createElement('div');
    el.className = `toast toast-${kind}`;
    el.textContent = text;
    const prio = TOAST_PRIO[kind] ?? 0;
    this.showNotice({ el, key: text, prio, ms: TOAST_MS[kind] ?? 2000 });
  }

  alert(text) {
    this.game?.vibrate([40, 60, 40]);
    this.toast(text, 'bad');
  }

  pushToast(el, ms, key = 'bonnetje') {
    this.showNotice({ el, key, prio: 10, ms }); // het bonnetje gaat voor op alles: het is het resultaat van je levering
  }

  showNotice(n) {
    const cur = this.notice;
    if (cur?.key === n.key || this.noticeQueue?.some((q) => q.key === n.key)) return;
    if (!cur) return this.displayNotice(n);
    if (n.prio > cur.prio) {
      this.endNotice(true, false);
      return this.displayNotice(n);
    }
    if (n.prio < 1) return; // gewone melding terwijl er al iets staat: overslaan
    this.noticeQueue ??= [];
    this.noticeQueue.push({ ...n, queuedAt: performance.now() });
    // Maximaal twee wachtend: de laagste en oudste valt weg
    if (this.noticeQueue.length > 2) {
      const drop = this.noticeQueue.reduce((lo, q) => (q.prio < lo.prio ? q : lo));
      this.noticeQueue.splice(this.noticeQueue.indexOf(drop), 1);
    }
  }

  displayNotice(n) {
    if (this.notice) {
      clearTimeout(this.notice.timer);
      this.notice.el.remove();
    }
    const box = $('toasts');
    // Rechtop komt de melding direct onder de bestellingen, zodat hij niet midden op de weg hangt.
    const portrait = matchMedia('(orientation: portrait)').matches;
    box.style.top = portrait ? `${Math.round(Math.max($('hud-offers').getBoundingClientRect().bottom, $('hud-top').getBoundingClientRect().bottom) + 8)}px` : '';
    box.replaceChildren(n.el);
    this.notice = n;
    document.body.classList.add('has-notice');
    n.timer = setTimeout(() => this.endNotice(false), n.ms);
  }

  /** @param {boolean} immediate meteen weg, zonder uitfaden; @param {boolean} [drain] daarna de wachtrij verder afwerken */
  endNotice(immediate, drain = true) {
    const cur = this.notice;
    if (!cur) return;
    clearTimeout(cur.timer);
    this.notice = null;
    const next = () => {
      if (!this.notice && drain) document.body.classList.remove('has-notice');
      cur.el.remove();
      // Wat te lang heeft gewacht is niet meer actueel
      while (drain && !this.notice && this.noticeQueue?.length) {
        // Het belangrijkste wachtende bericht eerst (bij gelijk: wie het eerst kwam)
        const best = this.noticeQueue.reduce((a, b) => (b.prio > a.prio ? b : a));
        this.noticeQueue.splice(this.noticeQueue.indexOf(best), 1);
        if (performance.now() - best.queuedAt < 4000) return this.displayNotice(best);
      }
    };
    if (immediate) return next();
    cur.el.style.transition = 'opacity .2s';
    cur.el.style.opacity = '0';
    setTimeout(next, 220);
  }

  // ---------------------------------------------------------------------------
  // Score delen
  // ---------------------------------------------------------------------------

  /** Deelt de score van de shift die net klaar is: het deelmenu van het apparaat, anders de link naar het klembord. */
  async shareScore() {
    const score = this.lastShare;
    if (!score) return;
    const url = shareUrl(`${location.origin}${location.pathname}`, score);
    const text = t('share.text', { money: euro(score.cents / 100), count: tn('share.deliveries', score.deliveries) });
    const btn = $('btn-share');
    const say = (msg) => {
      btn.textContent = msg;
      clearTimeout(this.shareTimer);
      this.shareTimer = setTimeout(() => (btn.textContent = t('report.share')), 2500);
    };
    try {
      if (navigator.share) {
        await navigator.share({ title: t('meta.title'), text, url });
        return;
      }
    } catch (e) {
      if (e?.name === 'AbortError') return; // de speler koos zelf om niet te delen
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      say(t('report.shareCopied'));
    } catch {
      say(t('report.shareFailed'));
    }
  }

  /**
   * Opent het scherm voor een gedeelde score (de waarde van ?share=). Een kapotte of onzin-link geeft een duidelijk
   * bericht in plaats van niets. Er gebeurt niets voordat de speler zelf op de knop drukt.
   */
  openShare(raw) {
    const score = parseShare(raw);
    this.sharedScore = score;
    const list = $('share-stats');
    const note = $('share-note');
    const play = $('btn-share-play');
    note.classList.add('hidden');
    if (!score) {
      $('share-title').textContent = t('share.invalidTitle');
      $('share-text').textContent = t('share.invalidText');
      list.replaceChildren();
      play.classList.add('hidden');
    } else {
      const today = score.daily && score.date === todayKey();
      const date = score.daily ? new Date(`${score.date}T00:00:00`).toLocaleDateString(getLanguage(), { day: 'numeric', month: 'long' }) : '';
      $('share-title').textContent = euro(score.cents / 100);
      $('share-text').textContent = score.daily ? t('share.introDaily', { date }) : t('share.intro');
      const stat = (label, value) => `<div class="stat"><dt>${esc(label)}</dt><dd>${esc(value)}</dd></div>`;
      list.innerHTML = [
        stat(t('report.delivered'), String(score.deliveries)),
        stat(t('share.dayStars'), starString(score.dayStars, 3)),
        stat(t('report.bestCombo'), score.bestCombo >= 2 ? `x${score.bestCombo}` : '–'),
        stat(t('share.rank'), RANKS[score.rank - 1].title),
      ].join('');
      // Een bewaarde shift blijft staan: een nieuwe shift starten zou die wissen
      const busy = !!this.game?.savedShift;
      play.classList.toggle('hidden', busy);
      play.textContent = today ? t('share.playDaily') : t('share.beat');
      this.sharedToday = today;
      const message = busy ? t('share.hasSaved') : score.daily && !today ? t('share.otherDay') : '';
      note.textContent = message;
      note.classList.toggle('hidden', !message);
    }
    this.open('share');
    requestAnimationFrame(() => (score && !play.classList.contains('hidden') ? play : $('btn-share-close')).focus({ preventScroll: true }));
  }

  /** "Probeer te verslaan": een nieuwe shift, met de score van de ander als doel in de afrekening. */
  playShared() {
    const score = this.sharedScore;
    if (!score || !this.game || this.game.savedShift) return;
    this.closeAll();
    this.challenge = { cents: score.cents };
    this.game.startShift({ daily: this.sharedToday });
  }

  showFine(fine) {
    $('fine-title').textContent = fine.title;
    $('fine-quote').textContent = `"${fine.quote}"`;
    $('fine-lines').innerHTML =
      fine.lines.map(([label, amount]) => `<div class="flex justify-between gap-3"><dt>${esc(label)}</dt><dd>${euro(amount)}</dd></div>`).join('') +
      `<div class="mt-1 flex justify-between gap-3 border-t border-dashed border-stone-400 pt-1 font-bold text-rose-700 dark:text-rose-300"><dt>${t('fine.total')}</dt><dd>${euro(fine.amount)}</dd></div>`;
    this.open('fine');
  }

  showReport(r) {
    this.lastWasDaily = !!r.daily;
    $('report-title').textContent = r.deliveries === 0 ? t('report.titleNone') : t(`report.title${r.shiftStars}`);
    $('report-stars').textContent = '★'.repeat(r.shiftStars) + '☆'.repeat(3 - r.shiftStars);
    $('report-stars').setAttribute('aria-label', t('report.starsAria', { n: r.shiftStars }));
    $('report-record').classList.toggle('hidden', !r.record);
    $('report-earnings').textContent = euro(r.earnings);
    $('report-deliveries').textContent = String(r.deliveries);
    $('report-avg').textContent = r.deliveries ? `${fmtNum(r.averageStars, 1)} ★` : '–';
    $('report-combo').textContent = r.bestCombo >= 2 ? `x${r.bestCombo}` : '–';
    $('report-stars-gained').textContent = `+${r.stars} ★`;
    $('report-missed').textContent = String(r.missed + r.lost);
    const nextGoal = r.target.find((g) => r.earnings < g);
    $('report-target').textContent = nextGoal ? t('report.nextGoal', { money: euro(nextGoal) }) : t('report.allGoals');

    const race = $('report-race');
    const raceTotal = r.raceWins + r.lost;
    race.classList.toggle('hidden', raceTotal === 0);
    if (raceTotal) race.textContent = t('report.race', { won: r.raceWins, lost: r.lost });

    const daily = $('report-daily');
    daily.classList.toggle('hidden', !r.daily);
    if (r.daily) {
      daily.textContent = r.daily.isBest ? t('report.dailyBest', { money: euro(r.daily.score) }) : t('report.dailyOther', { money: euro(r.daily.score), best: euro(r.daily.best) });
    }
    $('btn-next-shift').textContent = r.daily ? t('report.retry') : t('report.next');

    // Delen kan als er iets te delen valt; een uitdaging van een vriend wordt hier afgerekend (één keer)
    this.lastShare = { cents: Math.round(r.earnings * 100), deliveries: r.deliveries, dayStars: r.shiftStars, bestCombo: r.bestCombo, rank: r.rankAfter, daily: !!r.daily, date: todayKey() };
    const shareBtn = $('btn-share');
    shareBtn.classList.toggle('hidden', r.deliveries === 0);
    shareBtn.textContent = t('report.share');
    const challenge = $('report-challenge');
    challenge.classList.toggle('hidden', !this.challenge);
    if (this.challenge) {
      const goal = euro(this.challenge.cents / 100);
      challenge.textContent = r.earnings * 100 >= this.challenge.cents ? t('report.challengeMet', { goal }) : t('report.challengeMiss', { goal, left: euro(this.challenge.cents / 100 - r.earnings) });
      this.challenge = null;
    }
    const promoted = r.rankAfter > r.rankBefore;
    $('report-rank').classList.toggle('hidden', !promoted);
    if (promoted) {
      const rank = RANKS[r.rankAfter - 1];
      $('report-rank').textContent = t('report.promotion', { title: rank.title, districts: districtList(rank.districts.filter((d) => !RANKS[r.rankBefore - 1].districts.includes(d))) });
    }
    this.closeAll();
    this.open('report');
  }

  // ---------------------------------------------------------------------------
  // HUD
  // ---------------------------------------------------------------------------

  bindHud() {
    $('btn-pause').addEventListener('click', () => this.game?.pause());
    $('hud-offers').addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-offer]');
      if (!btn) return;
      if (btn.dataset.action === 'accept') this.game.acceptOffer(btn.dataset.offer);
      else this.game.declineOffer(btn.dataset.offer);
      this.renderOffers(true);
    });
  }

  acceptFirstOffer() {
    const first = this.game?.shift?.offers[0];
    if (first) {
      this.game.acceptOffer(first.id);
      this.renderOffers(true);
    }
  }

  declineFirstOffer() {
    const first = this.game?.shift?.offers[0];
    if (first) {
      this.game.declineOffer(first.id);
      this.renderOffers(true);
    }
  }

  /** Wordt elk beeld aangeroepen; werkt de HUD ongeveer 10 keer per seconde bij. */
  update(game) {
    const now = performance.now();
    if (now - this.lastHud < 100) return;
    this.lastHud = now;
    $('hud-wallet').textContent = euroShort(game.save.wallet);
    if (game.mode !== 'shift' || !game.shift) {
      $('hud-hint').classList.add('hidden');
      return;
    }
    const sh = game.shift;
    $('hud-clock').textContent = clockLabel(sh.t);
    $('hud-clock-ring').setAttribute('stroke-dasharray', `${(sh.remaining / SHIFT_SECONDS) * 100} 100`);
    $('hud-clock-ring').setAttribute('stroke', sh.remaining < 30 ? '#ef4444' : '#f59e0b');
    $('hud-earnings').textContent = euro(sh.earnings);
    $('hud-combo').classList.toggle('hidden', sh.combo < 2);
    $('hud-combo-text').textContent = `x${sh.combo}`;
    this.renderOffers(false);
    this.renderCargo(game);
    this.renderHint(game);

    // Randpijlen niet achter de aanbiedingen tekenen (rechtop staan die over de hele breedte).
    const topBar = $('hud-top').getBoundingClientRect().bottom;
    const offers = $('hud-offers').getBoundingClientRect();
    const fullWidth = offers.width > innerWidth * 0.6 && offers.height > 0;
    game.safeTop = Math.max(topBar, fullWidth ? offers.bottom : 0) + 8;
    const cargo = $('hud-cargo').getBoundingClientRect();
    const portrait = innerHeight > innerWidth;
    game.safeBottom = Math.max(this.baseSafeBottom ?? 24, portrait && cargo.height > 0 ? innerHeight - cargo.top + 8 : 0);
  }

  renderOffers(force) {
    const sh = this.game?.shift;
    if (!sh) return;
    const offers = sh.offers;
    const ids = offers.map((o) => o.id).join(',') + `|${sh.usedSlots}/${sh.capacity}`;
    const box = $('hud-offers');
    if (force || ids !== this.offerIds) {
      this.offerIds = ids;
      box.innerHTML = offers
        .map((o) => {
          const refusal = sh.canAccept(o.id);
          const p = o.product;
          return `
          <article class="offer" data-id="${o.id}">
            <div class="flex items-start gap-2.5">
              <span class="text-3xl leading-none" aria-hidden="true">${p.icon}</span>
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-extrabold">${esc(p.name)}</p>
                <p class="truncate text-xs font-semibold text-stone-600 dark:text-stone-300">${esc(o.customer)} · ${DISTRICT_NAMES[o.dest.districtId] ?? ''}</p>
                ${
                  o.race
                    ? `<p class="truncate text-xs font-extrabold text-rose-700 dark:text-rose-300">${t('offer.race', { bonus: euroShort(RACE_BONUS) })}</p>`
                    : `<p class="offer-tip truncate text-xs text-stone-500 dark:text-stone-400">${esc(p.tip)}</p>`
                }
              </div>
              <div class="text-right">
                <p class="font-mono text-sm font-extrabold text-emerald-700 dark:text-emerald-300">${euro(o.expectedPay)}</p>
                <p class="text-xs text-stone-500 dark:text-stone-400">${t('offer.tipLine', { sec: o.deadlineSec })}</p>
              </div>
            </div>
            <div class="flex gap-2">
              <button type="button" class="btn-primary min-h-11 flex-1 py-2 text-sm ${refusal ? 'opacity-50' : ''}" data-offer="${o.id}" data-action="accept">${refusal === 'vol' ? t('offer.full') : refusal === 'bruidstaart-alleen' ? t('offer.emptyFirst') : t('offer.accept')}</button>
              <button type="button" class="btn-secondary min-h-11 w-11 px-0 py-2" data-offer="${o.id}" data-action="decline" aria-label="${t('offer.decline')}">✕</button>
            </div>
            <i class="offer-expiry" data-expiry="${o.id}"></i>
          </article>`;
        })
        .join('');
    }
    for (const o of offers) {
      const bar = box.querySelector(`[data-expiry="${o.id}"]`);
      if (bar) bar.style.transform = `scaleX(${Math.max(0, (o.offerExpiresAt - sh.t) / 25)})`;
    }
  }

  renderCargo(game) {
    const sh = game.shift;
    const items = [...sh.accepted, ...sh.loaded];
    // Rustig scherm: alleen de bestelling met de minste tijd is helemaal te zien, de rest staat compact.
    const urgent = [...sh.loaded].sort((a, b) => timeLeft(a, sh.t) - timeLeft(b, sh.t))[0];
    const full = (o) => items.length === 1 || o === urgent || o.status !== 'ingeladen';
    const ids = items.map((o) => `${o.id}:${o.status}:${full(o) ? 'v' : 'c'}`).join(',');
    const box = $('hud-cargo');
    if (ids !== this.cargoIds) {
      this.cargoIds = ids;
      box.innerHTML = items
        .map((o) => {
          const loaded = o.status === 'ingeladen';
          const temp = o.product.heat !== 'geen';
          return `
          <div class="cargo-chip${full(o) ? '' : ' compact'}" data-cargo="${o.id}">
            <span class="text-2xl leading-none" aria-hidden="true">${o.product.icon}</span>
            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between gap-2">
                <span class="truncate text-xs font-extrabold" data-name>${o.race ? '🏁 ' : ''}${esc(o.customer)}</span>
                <span class="font-mono text-xs font-bold tabular-nums" data-time>${loaded ? '' : '🥖'}</span>
              </div>
              ${
                loaded
                  ? `<div class="mt-1 flex gap-1.5">
                      <div class="meter" title="${t('cargo.time')}"><i data-bar="time"></i></div>
                      ${temp ? `<div class="meter" title="${t(o.product.heat === 'koud' ? 'cargo.cold' : 'cargo.warm')}"><i data-bar="temp"></i></div>` : ''}
                      <div class="meter" title="${t('cargo.care')}"><i data-bar="care"></i></div>
                    </div>`
                  : `<p class="text-xs text-stone-600 dark:text-stone-300">${t('cargo.pickup')}</p>`
              }
            </div>
          </div>`;
        })
        .join('');
    }
    const colorblind = !!game.save.settings.colorblind;
    const c = meterColors(colorblind);
    for (const o of sh.loaded) {
      const chip = box.querySelector(`[data-cargo="${o.id}"]`);
      if (!chip) continue;
      const left = timeLeft(o, sh.t);
      const frac = Math.max(0, left / o.deadlineSec);
      const timeEl = chip.querySelector('[data-time]');
      timeEl.textContent = left >= 0 ? `${Math.ceil(left)}s` : `${colorblind ? '⚠ ' : ''}+${Math.ceil(-left)}s`;
      timeEl.className = `font-mono text-xs font-bold tabular-nums ${left < 0 ? 'text-rose-600 dark:text-rose-400' : ''}`;
      // Een balkje dat bijna leeg of in gevaar is, krijgt in de kleurenblindveilige stand ook strepen (klasse is-low)
      const set = (name, value, color, low) => {
        const bar = chip.querySelector(`[data-bar="${name}"]`);
        if (!bar) return;
        bar.style.width = `${Math.max(0, Math.min(100, value))}%`;
        bar.style.backgroundColor = color;
        bar.classList.toggle('is-low', low);
      };
      set('time', frac * 100, frac < 0.2 ? c.bad : frac < 0.5 ? c.mid : c.good, frac < 0.2);
      set('temp', o.temp, o.product.heat === 'koud' ? c.cold : c.warm, false);
      set('care', o.integrity, o.integrity < 50 ? c.bad : c.care, o.integrity < 50);
    }
  }

  /** Uitleg in de eerste twee shifts, telkens over de volgende stap. */
  renderHint(game) {
    const el = $('hud-hint');
    const sh = game.shift;
    let text = '';
    let step = null;
    if (game.guided) {
      const s = game.scooter;
      const near = (p) => p && (s.x - p.x) ** 2 + (s.y - p.y) ** 2 < 170 ** 2;
      step = guideStep({
        offers: sh.offers.length,
        accepted: sh.accepted.length,
        loaded: sh.loaded.length,
        nearBakery: near(game.targets.find((t) => t.kind === 'bakery')),
        nearCustomer: game.targets.some((t) => t.kind === 'customer' && near(t)),
        touch: document.documentElement.classList.contains('touch'),
      });
      if (step) text = t('hud.step', { n: step.n, of: step.of, text: step.text });
    } else if (game.save.stats.shifts < 2) {
      const touch = document.documentElement.classList.contains('touch');
      if (sh.loaded.length) text = t('hud.hintDeliver');
      else if (sh.accepted.length) text = t('hud.hintLoad');
      else if (sh.offers.length) text = touch ? t('hud.hintAcceptTouch') : t('hud.hintAcceptKey');
    }
    el.textContent = text;
    el.classList.toggle('hidden', !text);
    $('btn-guide-skip').classList.toggle('hidden', !game.guided);
    // De knop staat net boven de uitleg, wat die ook doet qua plek
    const skip = $('btn-guide-skip');
    skip.style.bottom = game.guided && text ? `${Math.round(innerHeight - el.getBoundingClientRect().top + 8)}px` : '';
    // Rechtop staat de uitleg net boven de ladingkaartjes, hoeveel rijen dat er ook zijn.
    const cargo = $('hud-cargo').getBoundingClientRect();
    el.style.bottom = text && innerHeight > innerWidth && cargo.height > 0 ? `${Math.round(innerHeight - cargo.top + 8)}px` : '';
  }

  // ---------------------------------------------------------------------------
  // Garage
  // ---------------------------------------------------------------------------

  renderGarage() {
    const s = this.game.save;
    $('garage-wallet').textContent = `👛 ${euro(s.wallet)}`;
    $('garage-stars').textContent = t('start.stars', { stars: tn('count.stars', s.stars) });
    const def = (id) => UPGRADES.find((u) => u.id === id);
    const row = (opt) => {
      const owned = opt.free || s.unlocked.includes(opt.id);
      const active = opt.active;
      const why = owned ? null : whyNotBuy(s, opt.id);
      const d = def(opt.id);
      let label;
      let hint = esc(opt.desc);
      if (active) label = t('garage.chosen');
      else if (owned) label = t('garage.choose');
      else if (why === 'te-weinig-sterren') {
        label = `★ ${d.minStars}`;
        hint += ` <b>${t('garage.needStars', { stars: tn('count.stars', d.minStars - s.stars) })}</b>`;
      } else {
        label = euro(d.price);
        if (why === 'te-weinig-geld') hint += ` <b>${t('garage.needMoney', { money: euro(d.price - s.wallet) })}</b>`;
      }
      const action = active ? '' : owned ? 'equip' : 'buy';
      const disabled = active || (!owned && why);
      return `
        <div class="flex items-center gap-3 rounded-2xl p-3 ${active ? 'bg-amber-100/80 dark:bg-amber-950/50' : 'bg-stone-100 dark:bg-white/5'} ${!owned && why === 'te-weinig-sterren' ? 'opacity-70' : ''}">
          <span class="text-2xl" aria-hidden="true">${opt.icon}</span>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-extrabold">${esc(opt.name)}</p>
            <p class="text-xs text-stone-600 dark:text-stone-300">${hint}</p>
          </div>
          <button type="button" class="${active ? 'btn-ghost' : owned ? 'btn-secondary' : 'btn-primary'} min-h-12 px-4 py-2 text-sm" data-garage="${opt.equipId}" data-buy="${opt.id}" data-action="${action}" ${disabled ? 'disabled' : ''}>${label}</button>
        </div>`;
    };
    const section = (title, opts) => `<div><p class="mb-2 text-xs font-bold uppercase tracking-wide text-stone-500 dark:text-stone-400">${title}</p><div class="space-y-2">${opts.map(row).join('')}</div></div>`;

    const cap = (box, veh) => tn('garage.capacity', capacityFor(box, veh));
    $('garage-list').innerHTML = [
      section(t('garage.vehicle'), [
        { id: 'scooter', equipId: 'scooter', free: true, icon: '🛵', name: t('garage.scooter'), desc: t('garage.scooterDesc'), active: s.vehicle === 'scooter' },
        { id: 'van', equipId: 'van', icon: '🚐', name: t('upgrade.van.name'), desc: t('garage.vanDesc', { capacity: cap(s.box, 'van') }), active: s.vehicle === 'van' },
      ]),
      section(s.vehicle === 'van' ? t('garage.boxOnScooter') : t('garage.box'), [
        { id: 'box_geen', equipId: 'box_geen', free: true, icon: '🫙', name: t('garage.noBox'), desc: t('garage.noBoxDesc', { capacity: cap('geenbak', 'scooter') }), active: s.box === 'geenbak' },
        { id: 'box_kleine', equipId: 'box_kleine', icon: '📦', name: t('upgrade.box_kleine.name'), desc: t('garage.smallBoxDesc', { capacity: cap('kleinebak', 'scooter') }), active: s.box === 'kleinebak' },
        { id: 'box_grote', equipId: 'box_grote', icon: '🧳', name: t('upgrade.box_grote.name'), desc: t('garage.bigBoxDesc', { capacity: cap('grotebak', 'scooter') }), active: s.box === 'grotebak' },
      ]),
      section(t('garage.wheels'), [
        { id: 'wheels_standaard', equipId: 'wheels_standaard', free: true, icon: '⚫', name: t('garage.standard'), desc: t('garage.standardWheelsDesc'), active: s.wheels === 'standaard' },
        { id: 'wheels_sport', equipId: 'wheels_sport', icon: '🛞', name: t('upgrade.wheels_sport.name'), desc: t('garage.sportWheelsDesc'), active: s.wheels === 'sport' },
      ]),
      section(t('garage.engine'), [
        { id: 'engine_standaard', equipId: 'engine_standaard', free: true, icon: '⚙️', name: t('garage.standard'), desc: t('garage.standardEngineDesc'), active: s.engine === 'standaard' },
        { id: 'engine_turbo', equipId: 'engine_turbo', icon: '⚡', name: t('upgrade.engine_turbo.name'), desc: t('garage.turboDesc'), active: s.engine === 'turbo' },
      ]),
      `<div><p class="mb-2 text-xs font-bold uppercase tracking-wide text-stone-500 dark:text-stone-400">${t('garage.paint')}</p><div class="flex flex-wrap gap-2">${PAINTS.map((p) => {
        const locked = p.minStars > s.stars;
        return `<button type="button" class="relative h-12 w-12 rounded-full ring-offset-2 ring-offset-stone-50 transition-transform active:scale-95 dark:ring-offset-stone-900 ${s.paint === p.code ? 'ring-4 ring-amber-500' : 'ring-1 ring-black/10'} ${locked ? 'opacity-40' : ''}" style="background:${p.code}" data-paint="${p.code}" aria-label="${locked ? t('garage.lockedFrom', { name: p.name, stars: tn('count.stars', p.minStars) }) : p.name}" aria-pressed="${s.paint === p.code}" ${locked ? 'disabled' : ''}>${locked ? '<span class="absolute inset-0 flex items-center justify-center text-sm" aria-hidden="true">🔒</span>' : ''}</button>`;
      }).join('')}</div><p class="mt-2 text-xs text-stone-600 dark:text-stone-300">${t('garage.paintHint')}</p></div>`,
    ].join('');

    $('garage-list').onclick = (e) => {
      const paint = e.target.closest('[data-paint]');
      if (paint && !paint.disabled) {
        const next = equip(this.game.save, paint.dataset.paint);
        if (next) this.game.setSave(next);
        this.renderGarage();
        return;
      }
      const btn = e.target.closest('[data-action]');
      if (!btn || btn.disabled) return;
      const cur = this.game.save;
      const next = btn.dataset.action === 'buy' ? buyUpgrade(cur, btn.dataset.buy) : equip(cur, btn.dataset.garage);
      if (!next) return;
      if (btn.dataset.action === 'buy') {
        this.game.sound.playCashRegister();
        setTimeout(() => this.game.sound.playUpgradeSound(), 180);
      } else {
        this.game.sound.playUiClick?.();
      }
      this.game.setSave(next);
      this.renderGarage();
      this.renderStart();
    };
  }

  renderAchievements() {
    const done = new Set(this.game.save.achievements);
    $('achievements-count').textContent = t('ach.count', { done: done.size, total: ACHIEVEMENTS.length });
    $('achievements-list').innerHTML = ACHIEVEMENTS.map((a) => {
      const got = done.has(a.id);
      return `<li class="flex items-center gap-3 rounded-2xl p-3 ${got ? 'bg-emerald-100/80 dark:bg-emerald-950/40' : 'bg-stone-100 dark:bg-white/5'}">
        <span class="text-2xl ${got ? '' : 'grayscale opacity-60'}" aria-hidden="true">${a.icon}</span>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-extrabold">${esc(a.name)}</p>
          <p class="text-xs text-stone-600 dark:text-stone-300">${esc(a.description)}</p>
        </div>
        <span class="whitespace-nowrap text-xs font-bold ${got ? 'text-emerald-800 dark:text-emerald-300' : 'text-stone-600 dark:text-stone-300'}">${got ? t('ach.got') : `+${euro(a.reward)}`}</span>
      </li>`;
    }).join('');
  }

  // ---------------------------------------------------------------------------
  // Instellingen, updates en installeren
  // ---------------------------------------------------------------------------

  renderSettings() {
    if (!this.game) return;
    const st = this.game.save.settings;
    for (const btn of document.querySelectorAll('[data-setting]')) {
      const value = String(st[btn.dataset.setting]);
      btn.setAttribute('aria-pressed', String(value === btn.dataset.value));
    }
    $('version-line').textContent = t('settings.version', { version: __APP_VERSION__, build: __APP_BUILD__ });
  }

  changeSetting(key, raw) {
    const g = this.game;
    const value = ['sound', 'assist', 'vibrate', 'minimap', 'colorblind'].includes(key) ? raw === 'true' : raw;
    g.save = { ...g.save, settings: { ...g.save.settings, [key]: value } };
    if (key === 'sound') g.sound.setEnabled(value);
    if (key in SOUND_SETTINGS) g.sound.setChannelLevel(SOUND_SETTINGS[key], levelGain(value));
    if (key === 'assist') g.scooter.steerAssist = value;
    if (key === 'vibrate' && value) navigator.vibrate?.(30);
    if (key === 'theme') g.setNightMode(value);
    if (key === 'motion') g.applyMotion();
    if (key === 'colorblind') this.applyColorblind(value);
    if (key === 'language') this.applyLanguage(value);
    if (key === 'controls') g.applyControlMode(value);
    g.persist();
    this.renderSettings();
  }

  showUpdate(updateFn) {
    this.updateFn = updateFn;
    this.renderUpdateLine();
    $('btn-update-now').classList.remove('hidden');
    $('update-toast').classList.remove('hidden');
    $('btn-update-reload').onclick = () => updateFn();
    $('btn-update-now').onclick = () => updateFn();
    $('btn-update-later').onclick = () => $('update-toast').classList.add('hidden');
  }

  renderUpdateLine() {
    $('update-line').textContent = t(this.updateFn ? 'settings.updateReady' : 'settings.upToDate');
  }

  setInstallPrompt(prompt) {
    this.installPrompt = prompt;
    $('btn-install').classList.toggle('hidden', !prompt);
  }

  async install() {
    if (this.installPrompt) {
      this.installPrompt.prompt();
      await this.installPrompt.userChoice.catch(() => {});
      this.setInstallPrompt(null);
    } else {
      this.toast(t('start.installHelp'), 'info');
    }
  }
}
